import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Select, Input, Space, Modal, Descriptions, Tabs, Image } from 'antd';
import { EyeOutlined, PlayCircleOutlined, StopOutlined, ReloadOutlined, FileImageOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { Search } = Input;
const { TabPane } = Tabs;

const JobsView = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobFiles, setJobFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    client: '',
    workflow: '',
    dateRange: null,
    search: '',
  });

  const statusColors = {
    'pending': 'orange',
    'running': 'blue',
    'completed': 'green',
    'failed': 'red',
    'cancelled': 'default',
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchJobs = async () => {
    try {
      setLoading(true);
      // Mock data for demo - replace with actual API call
      const mockJobs = [
        {
          id: 1,
          name: 'Data Processing Pipeline',
          workflow: 'Customer Analytics',
          client: 'client1',
          status: 'running',
          startTime: '2025-01-15T10:30:00Z',
          endTime: null,
          duration: null,
          progress: 65,
          logs: ['Started data processing', 'Processing batch 1/3', 'Current progress: 65%'],
        },
        {
          id: 2,
          name: 'Report Generation',
          workflow: 'Monthly Reports',
          client: 'client2',
          status: 'completed',
          startTime: '2025-01-15T09:00:00Z',
          endTime: '2025-01-15T09:45:00Z',
          duration: '45 minutes',
          progress: 100,
          logs: ['Started report generation', 'Processing data...', 'Report generated successfully'],
        },
        {
          id: 3,
          name: 'Data Validation',
          workflow: 'Quality Check',
          client: 'client1',
          status: 'failed',
          startTime: '2025-01-15T08:00:00Z',
          endTime: '2025-01-15T08:15:00Z',
          duration: '15 minutes',
          progress: 30,
          logs: ['Started validation', 'Error: Invalid data format detected', 'Job failed'],
          error: 'Invalid data format in row 1523',
        },
        {
          id: 4,
          name: 'Backup Process',
          workflow: 'System Backup',
          client: 'system',
          status: 'pending',
          startTime: null,
          endTime: null,
          duration: null,
          progress: 0,
          logs: ['Job queued for execution'],
        },
      ];
      setJobs(mockJobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...jobs];

    if (filters.status) {
      filtered = filtered.filter(job => job.status === filters.status);
    }

    if (filters.client) {
      filtered = filtered.filter(job => job.client === filters.client);
    }

    if (filters.workflow) {
      filtered = filtered.filter(job => 
        job.workflow.toLowerCase().includes(filters.workflow.toLowerCase())
      );
    }

    if (filters.search) {
      filtered = filtered.filter(job =>
        job.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.workflow.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const showJobDetails = (job) => {
    setSelectedJob(job);
    setIsDetailModalOpen(true);
    fetchJobFiles(job.id);
  };

  const fetchJobFiles = async (jobId) => {
    setLoadingFiles(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/jobs/${jobId}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setJobFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch job files:', error);
      setJobFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const getPreviewUrl = (fileId, size = 'medium') => {
    const token = localStorage.getItem('token');
    return `/api/files/${fileId}/preview/${size}?token=${token}`;
  };

  const handleJobAction = (jobId, action) => {
    // Mock implementation - replace with actual API calls
    console.log(`${action} job ${jobId}`);
    
    setJobs(prevJobs => 
      prevJobs.map(job => {
        if (job.id === jobId) {
          switch (action) {
            case 'start':
              return { ...job, status: 'running', startTime: new Date().toISOString() };
            case 'stop':
              return { ...job, status: 'cancelled', endTime: new Date().toISOString() };
            case 'restart':
              return { ...job, status: 'pending', progress: 0, endTime: null };
            default:
              return job;
          }
        }
        return job;
      })
    );
  };

  const columns = [
    {
      title: 'Job Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Workflow',
      dataIndex: 'workflow',
      key: 'workflow',
      sorter: (a, b) => a.workflow.localeCompare(b.workflow),
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      sorter: (a, b) => a.client.localeCompare(b.client),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status]}>{status.toUpperCase()}</Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress) => (
        <div style={{ width: 100 }}>
          <div style={{
            width: '100%',
            backgroundColor: '#f0f0f0',
            borderRadius: 4,
            overflow: 'hidden',
          }}>
            <div
              style={{
                width: `${progress}%`,
                backgroundColor: progress === 100 ? '#52c41a' : '#1890ff',
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 12,
              }}
            >
              {progress}%
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (time) => time ? new Date(time).toLocaleString() : '-',
      sorter: (a, b) => new Date(a.startTime) - new Date(b.startTime),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => duration || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => showJobDetails(record)}
          >
            View
          </Button>
          {record.status === 'pending' && (
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => handleJobAction(record.id, 'start')}
            >
              Start
            </Button>
          )}
          {record.status === 'running' && (
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleJobAction(record.id, 'stop')}
            >
              Stop
            </Button>
          )}
          {['failed', 'cancelled', 'completed'].includes(record.status) && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleJobAction(record.id, 'restart')}
            >
              Restart
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const uniqueClients = [...new Set(jobs.map(job => job.client))];

  return (
    <div>
      <Card title="Jobs View">
        {/* Filters */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Select
            placeholder="Filter by status"
            style={{ width: 150 }}
            allowClear
            value={filters.status}
            onChange={(value) => handleFilterChange('status', value)}
          >
            {Object.keys(statusColors).map(status => (
              <Option key={status} value={status}>
                <Tag color={statusColors[status]}>{status.toUpperCase()}</Tag>
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Filter by client"
            style={{ width: 150 }}
            allowClear
            value={filters.client}
            onChange={(value) => handleFilterChange('client', value)}
          >
            {uniqueClients.map(client => (
              <Option key={client} value={client}>{client}</Option>
            ))}
          </Select>

          <Input
            placeholder="Filter by workflow"
            style={{ width: 200 }}
            value={filters.workflow}
            onChange={(e) => handleFilterChange('workflow', e.target.value)}
          />

          <Search
            placeholder="Search jobs..."
            style={{ width: 200 }}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />

          <Button onClick={fetchJobs} icon={<ReloadOutlined />}>
            Refresh
          </Button>
        </div>

        {/* Jobs Table */}
        <Table
          columns={columns}
          dataSource={filteredJobs}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} jobs`,
          }}
        />
      </Card>

      {/* Job Details Modal */}
      <Modal
        title={`Job Details: ${selectedJob?.name}`}
        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setJobFiles([]);
        }}
        footer={null}
        width={1000}
      >
        {selectedJob && (
          <Tabs defaultActiveKey="details">
            <TabPane tab="Details" key="details">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Job Name" span={2}>
                  {selectedJob.name}
                </Descriptions.Item>
                <Descriptions.Item label="Workflow">
                  {selectedJob.workflow}
                </Descriptions.Item>
                <Descriptions.Item label="Client">
                  {selectedJob.client}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={statusColors[selectedJob.status]}>
                    {selectedJob.status.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Progress">
                  {selectedJob.progress}%
                </Descriptions.Item>
                <Descriptions.Item label="Start Time">
                  {selectedJob.startTime ? new Date(selectedJob.startTime).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="End Time">
                  {selectedJob.endTime ? new Date(selectedJob.endTime).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Duration">
                  {selectedJob.duration || '-'}
                </Descriptions.Item>
                {selectedJob.error && (
                  <Descriptions.Item label="Error" span={2}>
                    <span style={{ color: 'red' }}>{selectedJob.error}</span>
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Card title="Job Logs" style={{ marginTop: 16 }}>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {selectedJob.logs.map((log, index) => (
                    <div key={index} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ color: '#666', fontSize: 12 }}>
                        {new Date().toLocaleTimeString()}
                      </span>
                      <span style={{ marginLeft: 8 }}>{log}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </TabPane>

            <TabPane tab={<span><FileImageOutlined /> Files & Previews</span>} key="files">
              {loadingFiles ? (
                <div style={{ textAlign: 'center', padding: 40 }}>Loading files...</div>
              ) : jobFiles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  No files attached to this job
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                  {jobFiles.map((file) => (
                    <Card
                      key={file.id}
                      size="small"
                      hoverable
                      style={{ textAlign: 'center' }}
                      cover={
                        file.has_preview ? (
                          <div style={{ padding: 8, backgroundColor: '#f5f5f5', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Image
                              src={getPreviewUrl(file.id, 'thumbnail')}
                              alt={file.filename}
                              style={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain' }}
                              preview={{
                                src: getPreviewUrl(file.id, 'large')
                              }}
                              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                            />
                          </div>
                        ) : (
                          <div style={{ padding: 8, backgroundColor: '#f5f5f5', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FileImageOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                          </div>
                        )
                      }
                    >
                      <Card.Meta
                        title={<div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.filename}>
                          {file.filename}
                        </div>}
                        description={
                          <div style={{ fontSize: 11 }}>
                            <div>{(file.file_size / 1024).toFixed(1)} KB</div>
                            {file.has_preview && (
                              <Tag size="small" color="blue" style={{ marginTop: 4 }}>Preview Available</Tag>
                            )}
                          </div>
                        }
                      />
                    </Card>
                  ))}
                </div>
              )}
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default JobsView;
